import { BlotterPanelDocumentListItemLegacy } from 'components/shared/Blotter/BlotterPanel/blotter-panel-document-legacy/BlotterPanelDocumentListItemLegacy';
import { TinySpinner } from 'components/shared/TinySpinner/TinySpinner';
import { useDownloadDlpDocument } from 'hooks/useDownloadDlpDocument';
import { useTranslation } from 'hooks/useTranslation';
import type { IntlKey } from 'locales';
import type { AnalyticCategoryActions, WorkspaceAnalyticCategory } from 'models';
import { FC, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

import type { RegulationDocument } from '../model';

type DocumentsProps = {
  className?: string;
  labelKey: IntlKey;
  e2e: string;
  event: AnalyticCategoryActions[WorkspaceAnalyticCategory];
  hasErrors: boolean;
  documents: RegulationDocument[];
};

export function DocumentLink({
  language,
  uri,
  event,
  onDownload,
}: {
  language: string;
  uri: string;
  event: AnalyticCategoryActions[WorkspaceAnalyticCategory];
  onDownload?: () => void;
}) {
  const { formatMessage } = useIntl();
  const category = useContext(WorkspaceAnalyticCategoryContext);
  const langCode = formatMessage({ id: `languageCode.${language}` as any, defaultMessage: language });
  const onClick = useCallback(() => {
    logAnalytics.trackEvent(category, event, language);
    if (onDownload) {
      onDownload();
    }
  }, [category, event, language, onDownload]);

  return (
    <a
      key={language}
      className="text-link link-secondary px-2"
      data-e2e={'document-' + langCode}
      href={uri}
      target="_blank"
      onClick={onClick}
    >
      {langCode}
    </a>
  );
}

export function DocumentLinkWithToken({
  document,
  event,
}: {
  document: RegulationDocument;
  event: AnalyticCategoryActions[WorkspaceAnalyticCategory];
}) {
  const downloadTermsheetErrorMessage = useTranslation('downloadTermsheet.error');
  const { downloadDlpDocument, isDownloading } = useDownloadDlpDocument(downloadTermsheetErrorMessage);
  if (isDownloading) {
    return <TinySpinner className="mx-2 align-self-center" />;
  }
  return (
    <DocumentLink
      language={document.language}
      uri={document.uri}
      event={event}
      onDownload={() => {
        downloadDlpDocument(document.documentToken!);
      }}
    />
  );
}

export const RawDocuments: FC<DocumentsProps> = ({ documents, className, labelKey, e2e, event, hasErrors }) => {
  if (documents.length === 0 || hasErrors) {
    return null;
  }

  return (
    <BlotterPanelDocumentListItemLegacy nameKey={labelKey} className={className} e2e={e2e}>
      {documents.map(doc => {
        if (doc.public !== false) {
          return <DocumentLink key={doc.language} language={doc.language} uri={doc.uri} event={event} />;
        }
        return <DocumentLinkWithToken key={doc.language} document={doc} event={event} />;
      })}
    </BlotterPanelDocumentListItemLegacy>
  );
};

RawDocuments.displayName = 'RawTermsheetButton';
